import { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import './ContactNavigation.css';

// Only public marketing routes that previously shared the footer contact anchor.
// Account, admin, application, checkout and booking hashes keep their own meaning.
const LEGACY_CONTACT_PAGES = new Set([
  '/paperwork', '/webinars', '/services', '/tms', '/about', '/blog', '/resources',
  '/speakers', '/work-with-us', '/careers', '/partner-with-us', '/privacy-policy', '/terms-of-use',
]);
export function isLegacyContactAnchor(pathname, hash) {
  const path = pathname.replace(/\/+$/, '') || '/';
  return hash === '#contact' && (LEGACY_CONTACT_PAGES.has(path)
    || /^\/(services|webinars|speakers|resources|blog)\/[^/]+$/.test(path));
}

export default function ContactNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    // Native anchors and router links can both activate the current hash again.
    const repeatContact = event => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest?.('a[href]');
      if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
      const target = new URL(link.href, window.location.href);
      if (location.pathname === '/' && target.origin === window.location.origin
        && target.pathname === '/' && target.hash === '#contact') {
        event.preventDefault();
        if (location.hash === '#contact' && target.search === location.search) window.dispatchEvent(new Event('magellan:contact-requested'));
        else navigate({ pathname: '/', search: target.search, hash: '#contact' });
      }
    };
    document.addEventListener('click', repeatContact, true);
    return () => document.removeEventListener('click', repeatContact, true);
  }, [location.pathname, location.search, location.hash, navigate]);
  // /contact retains its separate redirect and attribution policy. Do not carry
  // package/payment selection parameters from legacy marketing hashes to Home.
  return isLegacyContactAnchor(location.pathname, location.hash) ? <Navigate replace to="/#contact" /> : null;
}

export function useHomeContactAnchor() {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname !== '/' || location.hash !== '#contact') return undefined;
    let disposeAttempt = () => {};
    const start = () => {
      disposeAttempt();
      let stopped = false;
      let frame;
      const timers = [];
      const interactions = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'input'];
      const dispose = () => {
        stopped = true;
        cancelAnimationFrame(frame);
        timers.forEach(clearTimeout);
        window.removeEventListener('magellan:page-ready', position);
        interactions.forEach(event => window.removeEventListener(event, dispose));
      };
      const position = () => {
        if (stopped) return;
        const card = document.getElementById('contact');
        if (!card || card.closest('[hidden]')) return;
        card.scrollIntoView({ block: 'start', behavior: 'instant' });
        card.focus({ preventScroll: true });
      };
      interactions.forEach(event => window.addEventListener(event, dispose, { passive: true }));
      window.addEventListener('magellan:page-ready', position);
      frame = requestAnimationFrame(position);
      // Bounded corrections cover route entrance and the card's in-view motion.
      // Any visitor interaction cancels all pending corrections immediately.
      [150, 450, 850, 1300].forEach(delay => timers.push(setTimeout(position, delay)));
      timers.push(setTimeout(dispose, 1800));
      disposeAttempt = dispose;
    };
    start();
    window.addEventListener('magellan:contact-requested', start);
    return () => {
      disposeAttempt();
      window.removeEventListener('magellan:contact-requested', start);
    };
  }, [location.pathname, location.hash, location.key]);
}
